'use client';

import { useRef } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER, ColumnPill, MonoCaption } from './bits';
import DossierHead from './DossierHead';
import { stampIn, useChoreo } from './motion';

/**
 * §01 — The Verdict. Hairline-split card, both columns decisive and true at
 * once: same arrow marker both sides, only the pill tints differ (honesty
 * rail: no winner declared).
 */
export default function VerdictSection({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-v-col]'), {
      y: 22,
      opacity: 0,
      duration: 0.7,
      stagger: 0.14,
    });
    stampIn(tl, root.querySelectorAll('[data-v-pill]'), '-=0.35');
    tl.from(
      root.querySelectorAll('[data-v-col="rii"] [data-v-item]'),
      { x: -16, opacity: 0, duration: 0.5, stagger: 0.07 },
      '-=0.1',
    );
    tl.from(
      root.querySelectorAll('[data-v-col="them"] [data-v-item]'),
      { x: -16, opacity: 0, duration: 0.5, stagger: 0.07 },
      '<',
    );
    tl.from(root.querySelector('[data-v-caption]'), { opacity: 0, duration: 0.45 }, '-=0.15');
    return tl;
  });

  const columns: { key: 'rii' | 'them'; pill: React.ReactNode; items: string[]; arrow: string }[] =
    [
      {
        key: 'rii',
        pill: <ColumnPill tone="brand">CHOOSE RII.LINK IF&hellip;</ColumnPill>,
        items: data.verdict.rii,
        arrow: 'text-brand-600',
      },
      {
        key: 'them',
        pill: <ColumnPill tone="neutral">CHOOSE {data.competitor.toUpperCase()} IF&hellip;</ColumnPill>,
        items: data.verdict.them,
        arrow: 'text-dim',
      },
    ];

  return (
    <section ref={rootRef} className="pt-14 md:pt-[72px]">
      <div className={CONTAINER}>
        <DossierHead
          num="01"
          label="THE VERDICT"
          title={data.verdict.title}
          lead={data.verdict.lead}
        />
        <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-px overflow-hidden rounded-ref-lg border border-line-2 bg-line-2 md:mt-10">
          {columns.map((col) => (
            <div
              key={col.key}
              data-v-col={col.key}
              className="flex flex-col gap-[18px] bg-surface px-6 py-[26px] md:px-[30px]"
            >
              <span data-v-pill className="self-start">
                {col.pill}
              </span>
              <div className="flex flex-col gap-[13px]">
                {col.items.map((item) => (
                  <span
                    key={item}
                    data-v-item
                    className="flex gap-3 text-[14.5px] leading-[1.55] text-ink"
                  >
                    <span aria-hidden="true" className={`flex-none font-jetbrains ${col.arrow}`}>
                      &rarr;
                    </span>
                    <span>{item}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <MonoCaption data-v-caption className="mt-3.5">
          {data.verdict.caption}
        </MonoCaption>
      </div>
    </section>
  );
}
